import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

import { InventoryItem, TransactionType } from "../../types/inventory";

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (adjustmentData: {
    reason: string;
    notes?: string;
    transactions: {
      ingredient_id: number;
      transaction_type: string;
      quantity: number;
      unit_cost?: number;
      notes?: string;
    }[];
  }) => void;
  inventoryItems: InventoryItem[];
  loading: boolean;
}

interface AdjustmentItem {
  id: string; // Client-side ID for tracking in the form
  ingredient_id: number;
  ingredient_name: string;
  unit: string;
  current_quantity: string;
  adjustment_quantity: string;
  transaction_type: TransactionType;
  unit_cost: string;
  notes: string;
}

const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  inventoryItems,
  loading,
}) => {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [adjustmentItems, setAdjustmentItems] = useState<AdjustmentItem[]>([]);
  const [selectedIngredientId, setSelectedIngredientId] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason("");
      setNotes("");
      setAdjustmentItems([]);
      setSelectedIngredientId("");
      setErrors({});
    }
  }, [isOpen]);

  // Add a new adjustment item
  const handleAddItem = () => {
    if (!selectedIngredientId) {
      setErrors((prev) => ({
        ...prev,
        ingredient: "Please select an ingredient",
      }));

      return;
    }

    const ingredientId = parseInt(selectedIngredientId);
    const ingredient = inventoryItems.find((item) => item.id === ingredientId);

    if (!ingredient) {
      setErrors((prev) => ({
        ...prev,
        ingredient: "Invalid ingredient selected",
      }));

      return;
    }

    // Create new adjustment item
    const newItem: AdjustmentItem = {
      id: `item-${Date.now()}`, // Generate a unique client-side ID
      ingredient_id: ingredientId,
      ingredient_name: ingredient.name,
      unit: ingredient.unit,
      current_quantity: ingredient.current_stock,
      adjustment_quantity: "",
      transaction_type: TransactionType.Adjustment,
      unit_cost: ingredient.cost_per_unit.toString(),
      notes: "",
    };

    setAdjustmentItems((prev) => [...prev, newItem]);
    setSelectedIngredientId("");
    setErrors((prev) => {
      const newErrors = { ...prev };

      delete newErrors.ingredient;

      return newErrors;
    });
  };

  // Remove an adjustment item
  const handleRemoveItem = (itemId: string) => {
    setAdjustmentItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Update an adjustment item field
  const handleItemChange = (
    itemId: string,
    field: keyof AdjustmentItem,
    value: string,
  ) => {
    setAdjustmentItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item,
      ),
    );

    // Clear error for this item if it exists
    if (errors[`${itemId}-${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        delete newErrors[`${itemId}-${field}`];

        return newErrors;
      });
    }
  };

  // Validate the form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!reason.trim()) {
      newErrors.reason = "Reason is required";
    }

    if (adjustmentItems.length === 0) {
      newErrors.items = "At least one adjustment item is required";
    }

    adjustmentItems.forEach((item) => {
      if (!item.adjustment_quantity) {
        newErrors[`${item.id}-adjustment_quantity`] = "Quantity is required";
      } else if (isNaN(parseFloat(item.adjustment_quantity))) {
        newErrors[`${item.id}-adjustment_quantity`] =
          "Quantity must be a number";
      }

      if (
        item.transaction_type === TransactionType.Purchase &&
        !item.unit_cost
      ) {
        newErrors[`${item.id}-unit_cost`] =
          "Unit cost is required for purchases";
      } else if (item.unit_cost && isNaN(parseFloat(item.unit_cost))) {
        newErrors[`${item.id}-unit_cost`] = "Unit cost must be a number";
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const adjustmentData = {
      reason: reason.trim(),
      notes: notes.trim() || undefined,
      transactions: adjustmentItems.map((item) => ({
        ingredient_id: item.ingredient_id,
        transaction_type: item.transaction_type,
        quantity: parseFloat(item.adjustment_quantity),
        unit_cost: item.unit_cost ? parseInt(item.unit_cost) : undefined,
        notes: item.notes.trim() || undefined,
      })),
    };

    try {
      await onSave(adjustmentData);
      onClose();
    } catch (err) {
      console.error("Error saving adjustment:", err);
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to save adjustment. Please try again.",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full h-[80vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            Stock Adjustment
          </h3>
        </div>

        <form
          className="flex flex-col flex-1 overflow-hidden"
          onSubmit={handleSubmit}
        >
          <div className="p-6 space-y-4 flex-1 overflow-y-auto">
            {/* Reason and Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  isRequired
                  errorMessage={errors.reason}
                  isInvalid={!!errors.reason}
                  label="Reason"
                  placeholder="Why is this adjustment needed?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div>
                <Input
                  label="Notes (Optional)"
                  placeholder="Additional details about this adjustment"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Overall error for items */}
            {errors.items && (
              <div className="text-red-600 dark:text-red-400 text-sm">
                {errors.items}
              </div>
            )}

            {/* Adjustment Items Section */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                Adjustment Items
              </h4>

              {/* Add new item selector */}
              <div className="flex items-end gap-2 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Ingredient
                  </label>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 px-3 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={selectedIngredientId}
                    onChange={(e) => setSelectedIngredientId(e.target.value)}
                  >
                    <option value="">Select an ingredient</option>
                    {inventoryItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.current_stock} {item.unit})
                      </option>
                    ))}
                  </select>
                  {errors.ingredient && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.ingredient}
                    </p>
                  )}
                </div>

                <Button
                  className="mb-[1px]"
                  color="primary"
                  onPress={handleAddItem}
                >
                  Add Item
                </Button>
              </div>

              {/* Items table */}
              {adjustmentItems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Ingredient
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Current Stock
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Adjustment
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Unit Cost
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Notes
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {adjustmentItems.map((item) => (
                        <tr key={item.id}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {item.ingredient_name}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {item.current_quantity} {item.unit}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div>
                              <Input
                                errorMessage={
                                  errors[`${item.id}-adjustment_quantity`]
                                }
                                isInvalid={
                                  !!errors[`${item.id}-adjustment_quantity`]
                                }
                                placeholder="Quantity"
                                size="sm"
                                type="text"
                                value={item.adjustment_quantity}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "adjustment_quantity",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <select
                              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1 px-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                              value={item.transaction_type}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "transaction_type",
                                  e.target.value as TransactionType,
                                )
                              }
                            >
                              <option value={TransactionType.Adjustment}>
                                Adjustment
                              </option>
                              <option value={TransactionType.Purchase}>
                                Purchase
                              </option>
                              <option value={TransactionType.Usage}>
                                Usage
                              </option>
                              <option value={TransactionType.Waste}>
                                Waste
                              </option>
                              <option value={TransactionType.Transfer}>
                                Transfer
                              </option>
                            </select>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <Input
                              disabled={
                                item.transaction_type !==
                                TransactionType.Purchase
                              }
                              errorMessage={errors[`${item.id}-unit_cost`]}
                              isInvalid={!!errors[`${item.id}-unit_cost`]}
                              placeholder="Cost"
                              size="sm"
                              type="text"
                              value={item.unit_cost}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "unit_cost",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <Input
                              placeholder="Notes"
                              size="sm"
                              type="text"
                              value={item.notes}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "notes",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              color="danger"
                              size="sm"
                              variant="light"
                              onPress={() => handleRemoveItem(item.id)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No items added. Select an ingredient to add.
                </div>
              )}
            </div>

            {/* Submit error */}
            {errors.submit && (
              <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg">
                {errors.submit}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <Button disabled={loading} variant="light" onPress={onClose}>
              Cancel
            </Button>
            <Button color="primary" isLoading={loading} type="submit">
              Save Adjustment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustmentModal;
